// @givmo/design-system — Avatar (SaaS layer).
//
// A circular user/entity avatar: renders an image when `src` is set, else initials on a tinted
// brand circle. Sizes sm/md/lg. Used in the shell account menu, tables, and activity lists.
import { useState } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface AvatarProps extends TestIdProps {
  /** Image URL; falls back to initials if absent or it fails to load. */
  src?: string | null;
  /** Full name (used for initials + alt text). */
  name?: string;
  /** Optional caller-derived fallback text; defaults to the design-system initials rule. */
  fallbackText?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Classes for the initials fallback alone — never the avatar's box — e.g. padding that keeps
   * the letters off the circle's edge. On the box, padding would shrink an image logo inside its
   * circle and show the box's fill beside it.
   */
  initialsClassName?: string;
}

const SIZE_CLASS: Record<NonNullable<AvatarProps['size']>, string> = {
  xs: 'size-24 text-s10',
  sm: 'size-32 text-s12',
  md: 'size-36 text-s13',
  lg: 'size-44 text-s15',
};

/**
 * True when a DOM image has already finished loading AND failed (zero intrinsic
 * width). Catches the SSR pre-hydration failure `onError` cannot observe.
 * Exported for unit tests (jsdom never loads images, so the component path
 * can't exercise it organically).
 */
export function imageAlreadySettledAsFailed(el: HTMLImageElement): boolean {
  return el.complete && el.naturalWidth === 0;
}

function initialsFor(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function Avatar({
  src,
  name,
  fallbackText,
  size = 'md',
  className,
  initialsClassName,
  testId,
}: AvatarProps) {
  // Remember WHICH src failed, not merely THAT one did. A boolean is sticky: once an
  // avatar's first URL 404s, a later `src` — a freshly uploaded logo, a locally
  // previewed object URL, another org's logo after a switch — is suppressed for the
  // life of the mounted component, so the image only ever appears after a remount.
  // Keying the failure to the URL retries on every change with no effect and no
  // flicker, and still falls back the moment that same URL fails again.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = src != null && src !== '' && failedSrc !== src;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        'bg-brand-100 font-sans font-w600 tracking-normal text-brand-700 select-none',
        SIZE_CLASS[size],
        className,
      )}
      {...testIdProp(testId)}
    >
      {showImage ? (
        <img
          src={src}
          alt={name ?? ''}
          className="size-full object-cover"
          // SSR race: a server-rendered <img> whose URL is dead can fire its error
          // event BEFORE React hydrates, so `onError` alone never sees it. The ref
          // runs at (post-hydration) mount and re-checks the already-settled state.
          ref={(el) => {
            if (el && imageAlreadySettledAsFailed(el)) setFailedSrc(src);
          }}
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <span
          aria-hidden
          className={initialsClassName}
          {...testIdProp(testId ? `${testId}-initials` : undefined)}
        >
          {fallbackText ?? initialsFor(name)}
        </span>
      )}
    </span>
  );
}
